import { useEffect, useRef, useState } from "react";
import { fetchTrending } from "@/lib/api";
import { formatScore } from "@/lib/types";

const FALLBACK = [
  "@binance 2.68",
  "@bnbchain 2.49",
  "@cz_binance 2.94",
  "@pancakeswap 1.66",
  "@a16zcrypto 2.81",
  "@VitalikButerin 2.72",
  "@heyibinance 2.77",
  "@cobie 2.55",
];

function segmentItems(items: string[], repeats: number) {
  return Array.from({ length: repeats }).flatMap((_, i) =>
    items.map((t, j) => `${t}--${i}-${j}`),
  );
}

/**
 * Infinite horizontal ticker. Prefers live trending scores; falls back to
 * static seeds when the API is offline.
 */
export function TickerStrip() {
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [repeats, setRepeats] = useState(3);
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    fetchTrending()
      .then((rows) => {
        if (!alive || rows.length === 0) return;
        setItems(
          rows.slice(0, 14).map((r) => `@${r.user.username} ${formatScore(r.score)}`),
        );
      })
      .catch(() => {
        /* keep FALLBACK */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const update = () => {
      const el = measureRef.current;
      if (!el) return;
      const oneSet = el.scrollWidth / repeats;
      const needed = Math.max(3, Math.ceil((window.innerWidth + oneSet) / oneSet));
      if (needed !== repeats) setRepeats(needed);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [repeats, items]);

  const segment = segmentItems(items, repeats);

  return (
    <div className="overflow-hidden border-t border-ink bg-dark">
      <div className="ticker-track flex items-center whitespace-nowrap py-3 will-change-transform">
        <div ref={measureRef} className="flex shrink-0 items-center">
          {segment.map((t) => (
            <span
              key={t}
              className="mono shrink-0 px-6 text-[11px] tracking-[0.16em] text-bg/70 uppercase sm:px-8 sm:text-[12px]"
            >
              <span className="mr-2 text-accent">/</span>
              {t.split("--")[0]}
            </span>
          ))}
        </div>
        <div className="flex shrink-0 items-center" aria-hidden="true">
          {segment.map((t) => (
            <span
              key={`dup-${t}`}
              className="mono shrink-0 px-6 text-[11px] tracking-[0.16em] text-bg/70 uppercase sm:px-8 sm:text-[12px]"
            >
              <span className="mr-2 text-accent">/</span>
              {t.split("--")[0]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
